import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
} from "@mui/material";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { ModalEditConfig } from "./ModalEditConfig";
import { useUserClippingConfig } from "../../../hooks/useClipping/useUserClippingConfig";

const Indicator = ({ value }) => (
  <Typography
    variant="body2"
    sx={{
      color: value ? "green" : "red",
      fontWeight: "bold",
      textAlign: "center",
    }}
  >
    {value ? "✔" : "✖"}
  </Typography>
);

export const ModalConfigClipping = ({ open, onClose, token, user }) => {
  if (!user) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxHeight: 600,
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant="h6" mb={2}>
          Configuración de clipping de cliente
        </Typography>

        <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Documento(s)</TableCell>
                <TableCell colSpan={4} align="center">Configuración de Documentos</TableCell>
                <TableCell colSpan={4} align="center">Configuración de Secciones</TableCell>
              </TableRow>
              <TableRow>
                <TableCell />
                <TableCell align="center">Portada</TableCell>
                <TableCell align="center">Índice</TableCell>
                <TableCell align="center">Separador</TableCell>
                <TableCell align="center">Acciones</TableCell>
                <TableCell align="center">Portada</TableCell>
                <TableCell align="center">Índice</TableCell>
                <TableCell align="center">Separador</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <UserRow user={user} token={token} />
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Modal>
  );
};

const UserRow = ({ user, token }) => {
  const documents = user.permissions?.map((p) => ({
    id: p.ID_DOCUMENTO,
    nombre: p.NOMBRE_DOCUMENTO,
  })) || [];

  const [refresh, setRefresh] = useState(false);
  const { loading, configDocs, configSections } = useUserClippingConfig(user.id, token, refresh);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [isEditConfig, setIsEditConfig] = useState(false);
  const [isSection, setIsSection] = useState(false);

  const openModal = (doc, isSection, config, isEdit) => {
    setIsSection(isSection);
    setSelectedDocId(doc.id);
    setSelectedConfig(config);
    setIsEditConfig(isEdit);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <TableRow>
        <TableCell colSpan={4}>
          <CircularProgress size={20} />
        </TableCell>
      </TableRow>
    );
  }

  if (documents.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={4}>
          <Typography variant="body2" sx={{ color: "gray", fontStyle: "italic" }}>
            Sin documentos asociados
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  const renderIndicators = (docId, configArray) => {
    const match = configArray.find((c) => c.Documento_ID === docId);
    return match ? (
      <>
        <TableCell><Indicator value={match.Portada} /></TableCell>
        <TableCell><Indicator value={match.Indice} /></TableCell>
        <TableCell><Indicator value={match.Separador} /></TableCell>
      </>
    ) : (
      <>
        <TableCell><Indicator value={false} /></TableCell>
        <TableCell><Indicator value={false} /></TableCell>
        <TableCell><Indicator value={false} /></TableCell>
      </>
    );
  };

  const renderAction = (doc, configArray, isSection = false) => {
    const match = configArray.find((c) => c.Documento_ID === doc.id);
    const config = match ? match : null;
    const isEdit = !!match;
  
    return (
      <TableCell align="center">
        <IconButton
          size="small"
          onClick={() => openModal(doc, isSection, config, isEdit)}
        >
          {isEdit ? <EditIcon color="primary" /> : <AddIcon color="action" />}
        </IconButton>
      </TableCell>
    );
  };

  return (
    <>
      {documents.map((doc, index) => {
        const isLast = index === documents.length - 1;
        const borderStyle = isLast ? "1px solid rgba(224, 224, 224, 1)" : "none";

        return (
          <TableRow key={`${user.id}-${doc.id ?? index}`}>
            <TableCell sx={{ borderBottom: borderStyle }}>{doc.nombre}</TableCell>
            {renderIndicators(doc.id, configDocs)}
            {renderAction(doc, configDocs, false)}
            {renderIndicators(doc.id, configSections)}
            {renderAction(doc, configSections, true)}
          </TableRow>
        );
      })}
      <ModalEditConfig
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        token={token}
        userId={user.id}
        documentId={selectedDocId}
        config={selectedConfig}
        isEdit={isEditConfig}
        isSection={isSection}
        refresh={() => setRefresh(!refresh)}
      />
    </>
  );
};
